import React, {useEffect, useState, useMemo} from 'react';
import {useParams, Link as RouterLink} from 'react-router-dom';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {
    Box,
    Typography,
    Link,
    Chip,
    Button,
    Alert,
    CircularProgress,
    Paper,
    Stack,
    Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import ComputerIcon from '@mui/icons-material/Computer';
import CategoryIcon from '@mui/icons-material/Category';
import apiService from '../../services/apiService';
import {getStatusColor, getWinningColor, formatPlayerName} from '../../utils/formatters';

// --- Sub-Components for Readability ---

const SessionInfoCard = ({session}) => (
    <Paper elevation={2} sx={{p: 2, mb: 3}}>
        <Typography variant="h6" gutterBottom>Session #{session.id}</Typography>
        <Divider sx={{mb: 2}}/>
        <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography component="strong" sx={{minWidth: '100px'}}><strong>ID:</strong></Typography>
                <Typography sx={{wordBreak: 'break-all'}}>{session.id}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography component="strong"><strong>Created At:</strong></Typography>
                <Typography>{session.createdAt ? new Date(session.createdAt).toLocaleString() : '—'}</Typography>
            </Stack>
            <Divider sx={{my: 1}}/>
            <Stack direction="row" spacing={1} alignItems="center">
                <CategoryIcon fontSize="small" color="action"/>
                <Typography component="strong">Category:</Typography>
                <Link component={RouterLink} to={`/categories/${session.categoryId}`}>{session.categoryId}</Link>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon fontSize="small" color="action"/>
                <Typography component="strong">Player:</Typography>
                <Link component={RouterLink} to={`/players/${session.playerId}`}>{session.playerId}</Link>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <ComputerIcon fontSize="small" color="action"/>
                <Typography component="strong">Machine:</Typography>
                <Link component={RouterLink} to={`/machines/${session.machineId}`}>{session.machineId}</Link>
            </Stack>
        </Stack>
    </Paper>
);

const SessionGamesTable = ({gameSessionId}) => {
    const [games, setGames] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        apiService.get(`/api/game-sessions/${gameSessionId}/games`)
            .then(data => setGames(data))
            .catch(err => setError(err.message || 'Failed to load games.'))
            .finally(() => setIsLoading(false));
    }, [gameSessionId]);

    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'Game',
            size: 120,
            Cell: ({cell}) => <Link component={RouterLink}
                                    to={`/games/${cell.getValue()}`}>{cell.getValue().slice(0, 8)}…</Link>
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({cell}) => <Chip label={cell.getValue()} color={getStatusColor(cell.getValue())} size="small"/>
        },
        {
            accessorKey: 'winning',
            header: 'Result',
            Cell: ({cell}) => <Chip label={cell.getValue() || 'N/A'} color={getWinningColor(cell.getValue())}
                                    size="small"/>
        },
        {accessorKey: 'startedAt', header: 'Started', Cell: ({cell}) => new Date(cell.getValue()).toLocaleString()},
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: games,
        state: {isLoading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {showGlobalFilter: true, sorting: [{id: 'startedAt', desc: false}]},
    });

    return <MaterialReactTable table={table}/>;
};


// --- Main Page Component ---

const GameSessionDetailPage = () => {
    const {gameSessionId} = useParams();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        apiService.get(`/api/game-sessions/${gameSessionId}`)
            .then(sessionData => setSession(sessionData))
            .catch(err => setError(err.message || 'Failed to load session data.'))
            .finally(() => setLoading(false));
    }, [gameSessionId]);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;
    if (!session) return <Alert severity="info" sx={{m: 2}}>No such session found.</Alert>;

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Game Session Details</Typography>

            <SessionInfoCard session={session}/>

            <Typography variant="h5" gutterBottom>Games in this Session</Typography>
            <SessionGamesTable gameSessionId={gameSessionId}/>

            <Box sx={{mt: 3}}>
                <Button component={RouterLink} to="/game-sessions" variant="outlined" startIcon={<ArrowBackIcon/>}>
                    Back to Sessions
                </Button>
            </Box>
        </Box>
    );
};

export default GameSessionDetailPage;